import React from 'react';
type Tome = {
  number: string;
  title: string;
  weeks: string;
  framework: string;
  promise: string;
  badge: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'Build the Room',
  weeks: 'Practices 01–15',
  framework: 'The Two-Track Practice System · The Stance-Motion-Level Ladder',
  promise: 'Stance, motion, safe falls and the first takedown, ride and pin.',
  badge: 'bg-sage',
  contents: ['Practice 01: day one, with a warm-up game everyone can play', 'Folkstyle scoring for new coaches: what earns points and what costs them', 'Illegal and potentially dangerous holds: what to stop in the room', 'Room & Mat Setup Map and Two-Track Roster Split worksheets', 'Twelve warm-up games you can explain in 30 seconds', 'Running 30+ wrestlers with one coach and one mat']
}, {
  number: '02',
  title: 'Attack on Your Feet',
  weeks: 'Practices 16–30',
  framework: 'The Setup-Shot-Finish Ladder · The Inside-First Rule',
  promise: 'Setups, takedowns, finishes and neutral defense, in the order they build.',
  badge: 'bg-block-a',
  contents: ['Snap-down, arm drag, duck under, high crotch and ankle pick, each with its setup', 'Three ways to finish a single leg', 'Whizzer, down-blocks and what to do when the first shot fails', 'Neutral situations on the clock: 30-second goes', 'Takedown Check-Off and Drill Partner Pairing Chart', 'Neutral drills for small rooms, big groups and mixed rooms']
}, {
  number: '03',
  title: 'Own the Mat',
  weeks: 'Practices 31–45',
  framework: 'The Escape Order · The Break-Ride-Turn Sequence',
  promise: 'Escapes, rides, pins and a real plan for the holiday break.',
  badge: 'bg-block-c',
  contents: ['Stand-up, sit-out, switch and Granby roll with safe progressions', 'Breakdowns, spiral ride and legal mat returns', 'Cradles, half nelson and tilts for back points', 'Holiday-break practice: an open-mat day with a real plan', 'Back from the break: get the room moving again', 'Mat Rotation Planner for limited mat space']
}, {
  number: '04',
  title: 'Chain It Together',
  weeks: 'Practices 46–60',
  framework: 'The Situation Deck · The Match-Week Dial',
  promise: 'Connect the moves, then practice the moments that decide matches.',
  badge: 'bg-block-b',
  contents: ['Takedown into breakdown, escape into attack, three-point chains', 'Down by one with 30 seconds left. Up by one. Sudden victory', 'The first practice after a meet, and the one hard day of the week', 'Day before a dual and day before a tournament, short and sharp', 'Match-Week Planner and Tournament Day Coach Checklist', 'The room when half the team is away: a JV and rookie day']
}, {
  number: '05',
  title: 'Hold the Line',
  weeks: 'Practices 61–75',
  framework: 'The Reteach Loop · The Keep-It-Fresh Rotation',
  promise: "Midseason reteach that doesn't bore the room, even with short staff.",
  badge: 'bg-smoke',
  contents: ['Stance and motion re-check: the drill that exposes bad habits', "Assistant-led practice for when the head coach can't be there", 'Half-mat practice: big group, small space', 'Mixed rooms: partner rules and pairings when girls and boys train together', 'Rookie showcase: rookies lead the drill', 'Twelve fresh games and one-line cues for every reteach drill']
}, {
  number: '06',
  title: 'Peak for the Postseason',
  weeks: 'Practices 76–90',
  framework: "Sharpen, Don't Grind · The Two-Track Peak Split",
  promise: 'Less volume, same speed, no new moves — then a clean start to the offseason.',
  badge: 'bg-gold-deep',
  contents: ['Best-position day: drill your top three', 'Wrestling the same opponent a second time; wrestling back after a bracket loss', 'Day before state: the shortest practice of the year', 'Offseason starters: freestyle, par terre and a youth club night', 'Season Review and Offseason Plan Handout', 'Plan next season in one hour']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-cream" data-id="element-271" data-mp="3h6yrkhqbe4h7">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-272" data-mp="3h5nl2a5hiime">
        <div className="text-center" data-id="element-273" data-mp="3uxbzwl74bl72">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-gold-deep" data-id="element-274" data-mp="3v0gbw4qtrl8a">
            The complete season
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display font-semibold text-plum text-3xl sm:text-5xl" data-id="element-275" data-mp="8roslihq1fdn">
            Six volumes. <span className="text-gold-deep" data-id="element-276" data-mp="3pzgdzirivnz">Ninety practices.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-277" data-mp="2aj1urzsb1960">
            Each volume covers about three weeks of the season and works on its
            own: its clock, warm-up games, finishers and worksheets are all
            inside it. Starting midseason? Open the volume that matches your
            room today.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-278" data-mp="bc3bropxkn8k">
          {tomes.map(tome => <article key={tome.number} className="flex flex-col rounded-3xl border border-cream-dark bg-white p-6 shadow-card" data-id="element-279" data-mp="g9weu8vxt14m">
              <div className="flex items-center gap-2.5" data-id="element-280" data-mp="6cic1c2xsaus">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full font-display text-base font-semibold text-white ${tome.badge}`} data-id="element-281" data-mp="6e1rj0grqhvi">
                  {tome.number}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-muted" data-id="element-282" data-mp="1mr016zq11egg">
                  Volume {tome.number} · {tome.weeks}
                </span>
              </div>
              <h3 className="mt-3 font-display text-2xl font-semibold leading-snug text-plum" data-id="element-283" data-mp="1q8eblv42rbuw">
                {tome.title}
              </h3>
              <p className="mt-1 text-[12px] font-semibold uppercase tracking-wider text-gold-deep" data-id="element-284" data-mp="1l8rzxq3wa3o7">
                {tome.framework}
              </p>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-285" data-mp="1bbeyjrqgeqt1">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-cream-dark pt-4" data-id="element-286" data-mp="1bdhwqz1rfq8u">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[13px] text-ink" data-id="element-287" data-mp="240cevpb3udj5">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-gold" data-id="element-288" data-mp="242f2k2osbukp" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}